import BaseService from '../BaseService';
import type { FilterModel } from 'ag-grid-community';
import { convertFilterModelToBackend } from '@/queries/ssrm/convertFilterModelToBackend';
import type { SsrmMetricsResponse, SsrmRequestBody } from './CncSummaryMetricsService';

export interface AgaMetricsDateRange {
  start: Date;
  end: Date;
  label: string;
}

export interface AuthorizationUserMetricsRow {
  id?: string;
  type?: string;
  status?: string;
  createdAt?: string;
  roleId?: string | string[];
  appId?: string | string[];
}

const CNC_EXTERNAL_CLIENT_ROLE_IDS = ['c&c_client_read', 'c&c_client_read_/_write'] as const;
const CNC_METRICS_APP_ID = 'cnc';
const RESI_EXTERNAL_CLIENT_ROLE_IDS = ['client_read_only', 'client_read_/_write'] as const;
const RESI_METRICS_APP_ID = 'resi';
const SSRM_PAGE_SIZE = 1000;

function toDateFilterString(date: Date, useEndOfDay = false): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return useEndOfDay ? `${year}-${month}-${day} 23:59:59` : `${year}-${month}-${day} 00:00:00`;
}

function buildSetFilter(field: string, values: readonly string[]): Record<string, unknown> {
  return {
    [field]: {
      filterType: 'set',
      values: [...values],
    },
  };
}

function buildDateRangeFilter(field: string, start: Date, end: Date): Record<string, unknown> {
  return {
    [field]: {
      filterType: 'date',
      type: 'inRange',
      dateFrom: toDateFilterString(start),
      dateTo: toDateFilterString(end, true),
    },
  };
}

function mergeFilterModels(...models: Record<string, unknown>[]): Record<string, unknown> {
  return models.reduce<Record<string, unknown>>((acc, model) => ({ ...acc, ...model }), {});
}

async function fetchAllSsrmRows<TRow>(
  fetchPage: (startRow: number, endRow: number) => Promise<SsrmMetricsResponse<TRow>>,
): Promise<TRow[]> {
  const probe = await fetchPage(0, 1);
  const total = probe.rowCount ?? probe.rowData.length;

  if (total <= 0) {
    return [];
  }

  if (total <= SSRM_PAGE_SIZE) {
    const response = await fetchPage(0, total);
    return response.rowData ?? [];
  }

  const rows: TRow[] = [];
  for (let start = 0; start < total; start += SSRM_PAGE_SIZE) {
    const end = Math.min(start + SSRM_PAGE_SIZE, total);
    const response = await fetchPage(start, end);
    rows.push(...(response.rowData ?? []));
  }

  return rows;
}

class AuthorizationUserMetricsService extends BaseService {
  constructor() {
    super('/api/authorization-service');
  }

  getSsrmPage = (request: SsrmRequestBody): Promise<SsrmMetricsResponse<AuthorizationUserMetricsRow>> =>
    this.post<SsrmRequestBody, SsrmMetricsResponse<AuthorizationUserMetricsRow>>({
      path: 'users/ssrm',
      body: request,
    });

  fetchExternalClientRows = (
    appId: string,
    roleIds: readonly string[],
    range: AgaMetricsDateRange,
  ): Promise<AuthorizationUserMetricsRow[]> => {
    const filterModel = mergeFilterModels(
      buildSetFilter('type', ['External']),
      buildSetFilter('appId', [appId]),
      buildSetFilter('roleId', roleIds),
      buildDateRangeFilter('createdAt', new Date(0), range.end),
    );

    return fetchAllSsrmRows((startRow, endRow) =>
      this.getSsrmPage({
        startRow,
        endRow,
        rowGroupCols: [],
        groupKeys: [],
        valueCols: [],
        pivotCols: [],
        pivotMode: false,
        filterModel,
        sortModel: [{ colId: 'createdAt', sort: 'asc' }],
        rowIdExtractor: 'id',
        convertedFilterModel: convertFilterModelToBackend(filterModel as FilterModel),
      }),
    );
  };

  fetchCncExternalClientRows = (range: AgaMetricsDateRange): Promise<AuthorizationUserMetricsRow[]> =>
    this.fetchExternalClientRows(CNC_METRICS_APP_ID, CNC_EXTERNAL_CLIENT_ROLE_IDS, range);

  fetchResiExternalClientRows = (range: AgaMetricsDateRange): Promise<AuthorizationUserMetricsRow[]> =>
    this.fetchExternalClientRows(RESI_METRICS_APP_ID, RESI_EXTERNAL_CLIENT_ROLE_IDS, range);
}

export default AuthorizationUserMetricsService;
